import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Plus, RefreshCw, Users, X } from "lucide-react";
import type { ExtractedAudience } from "./types";

type Existing = { id: number; name: string };

export type AudiencesSectionProps = {
  audiences: ExtractedAudience[];
  existingAudiences: Existing[];
  appliedIdx: Set<number>;
  discardedIdx: Set<number>;
  isPending: boolean;
  onApply: (idx: number, audience: ExtractedAudience) => void;
  onDiscard: (idx: number) => void;
};

function findExisting(existing: Existing[], name: string): Existing | undefined {
  const norm = name.trim().toLowerCase();
  return existing.find((a) => a.name.trim().toLowerCase() === norm);
}

export function AudiencesSection({
  audiences,
  existingAudiences,
  appliedIdx,
  discardedIdx,
  isPending,
  onApply,
  onDiscard,
}: AudiencesSectionProps) {
  const visible = audiences.map((a, i) => ({ a, i })).filter(({ i }) => !discardedIdx.has(i));
  if (visible.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-indigo-600" />
        <h4 className="font-semibold text-sm">Públicos-alvo detectados ({visible.length})</h4>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {visible.map(({ a: audience, i: idx }) => {
          const applied = appliedIdx.has(idx);
          const existing = findExisting(existingAudiences, audience.name);
          const isUpdate = !!existing;
          return (
            <Card key={idx} className="p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-sm font-medium truncate">{audience.name}</p>
                    {isUpdate && !applied && (
                      <Badge variant="outline" className="text-[9px] border-amber-300 bg-amber-50 text-amber-700">
                        já existe
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1 mt-0.5 flex-wrap">
                    {audience.preferredTone && (
                      <Badge variant="secondary" className="text-[10px]">{audience.preferredTone}</Badge>
                    )}
                    {audience.role && <Badge variant="outline" className="text-[10px]">{audience.role}</Badge>}
                  </div>
                </div>
                {applied ? (
                  <Badge variant="default" className="shrink-0 bg-emerald-100 text-emerald-700 border-emerald-200">
                    <Check className="w-3 h-3 mr-1" /> {isUpdate ? "Atualizado" : "Criado"}
                  </Badge>
                ) : (
                  <div className="flex gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant={isUpdate ? "default" : "outline"}
                      className="h-7 px-2"
                      onClick={() => onApply(idx, audience)}
                      disabled={isPending}
                    >
                      {isUpdate ? (
                        <><RefreshCw className="w-3 h-3 mr-1" /> Atualizar</>
                      ) : (
                        <><Plus className="w-3 h-3 mr-1" /> Criar</>
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-muted-foreground"
                      title="Descartar sugestão"
                      onClick={() => onDiscard(idx)}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
              {audience.description && (
                <p className="text-xs text-muted-foreground line-clamp-2">{audience.description}</p>
              )}
              <div className="flex gap-3 text-[10px] text-muted-foreground">
                {audience.painPoints?.length ? <span>{audience.painPoints.length} dores</span> : null}
                {audience.desires?.length ? <span>{audience.desires.length} desejos</span> : null}
                {audience.commonObjections?.length ? <span>{audience.commonObjections.length} objeções</span> : null}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
